import ApiService from "../../../services/ApiService";
import { resolveApiFetchUrl } from "../../../services/ApiBaseUrl";
import { apiFetch } from "../../../services/apiFetch";
import {
    clearWordWheelSession,
    getOrCreateWordWheelSession,
    getWordWheelSession,
} from "./session";
import { getRevenueCatIdentity } from "./revenueCatPurchases";

async function isLoggedIn() {
    try {
        await ApiService.getAccessToken();
        return Boolean(ApiService.init.headers.get("Authorization"));
    } catch {
        return false;
    }
}

/** Logged-in users only send an existing session once so the server can migrate anonymous plays. */
async function getSessionForRequest() {
    const loggedIn = await isLoggedIn();
    if (loggedIn) {
        return { session: getWordWheelSession(), loggedIn: true };
    }
    return { session: getOrCreateWordWheelSession(), loggedIn: false };
}

function clearSessionAfterLoginMigration(loggedIn, session) {
    if (loggedIn && session?.sessionId) {
        clearWordWheelSession();
    }
}

async function attachOptionalRevenueCatIdentity(payload) {
    try {
        const ids = await Promise.race([
            getRevenueCatIdentity(),
            new Promise((resolve) =>
                setTimeout(
                    () =>
                        resolve({
                            revenueCatAppUserId: "",
                            revenueCatOriginalAppUserId: "",
                        }),
                    400
                )
            ),
        ]);
        if (ids?.revenueCatAppUserId) {
            payload.revenueCatAppUserId = ids.revenueCatAppUserId;
        }
        if (ids?.revenueCatOriginalAppUserId) {
            payload.revenueCatOriginalAppUserId = ids.revenueCatOriginalAppUserId;
        }
    } catch {
        /* native bridge missing on old shells — play must still work */
    }
    return payload;
}

async function optionalAuthHeaders() {
    const headers = new Headers();
    headers.set("Content-Type", "application/json");
    try {
        await ApiService.getAccessToken();
        const bearer = ApiService.init.headers.get("Authorization");
        if (bearer) {
            headers.set("Authorization", bearer);
        }
    } catch {
        /* guest — omit Authorization */
    }
    return headers;
}

async function parseJsonResponse(result) {
    const json = await result.json();
    if (!result.ok) {
        const error = new Error(json.message || json.error || `HTTP ${result.status}: ${result.statusText}`);
        error.status = result.status;
        error.response = json;
        throw error;
    }
    return json;
}

async function getOptionalAuth(url, param) {
    const headers = await optionalAuthHeaders();
    let queryString = url;
    if (param) {
        const entities = [];
        for (const [key, value] of Object.entries(param)) {
            if (value || value === 0) {
                entities.push(`${key}=${encodeURIComponent(value)}`);
            }
        }
        if (entities.length > 0) {
            queryString = `${url}?${entities.join("&")}`;
        }
    }
    const result = await apiFetch(resolveApiFetchUrl(queryString), { method: "GET", headers });
    return parseJsonResponse(result);
}

async function postOptionalAuth(url, data) {
    const headers = await optionalAuthHeaders();
    const result = await apiFetch(resolveApiFetchUrl(url), {
        method: "POST",
        headers,
        body: JSON.stringify(data),
    });
    return parseJsonResponse(result);
}

async function putOptionalAuth(url, data) {
    const headers = await optionalAuthHeaders();
    const result = await apiFetch(resolveApiFetchUrl(url), {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
    });
    return parseJsonResponse(result);
}

const WordWheelApi = {
    fetchNewest: () => getOptionalAuth("/v1/puzzle/wordwheel/newest"),

    fetchNext: async () => {
        const { session, loggedIn } = await getSessionForRequest();
        const data = await getOptionalAuth(
            "/v1/puzzle/wordwheel/next",
            session ? { sessionId: session.sessionId } : undefined
        );
        clearSessionAfterLoginMigration(loggedIn, session);
        return data;
    },

    fetchDaily: async (date) => {
        const { session, loggedIn } = await getSessionForRequest();
        const param = {};
        if (session) {
            param.sessionId = session.sessionId;
        }
        if (date) {
            param.date = date;
        }
        const data = await getOptionalAuth(
            "/v1/puzzle/wordwheel/daily",
            Object.keys(param).length > 0 ? param : undefined
        );
        clearSessionAfterLoginMigration(loggedIn, session);
        return data;
    },

    fetchJourneyLevel: async (level, season) => {
        const param = { level: String(level) };
        if (season) {
            param.season = season;
        }
        return getOptionalAuth("/v1/puzzle/wordwheel/journey", param);
    },

    /** Published puzzle by template id (`GET /v1/puzzle/wordwheel/{id}`). */
    fetchPublishedById: (tplId) =>
        getOptionalAuth(`/v1/puzzle/wordwheel/${encodeURIComponent(tplId)}`),

    /** Admin template by id (`GET /v1/puzzle/wordwheel-tpl/{id}` — requires login). */
    fetchById: (tplId) => ApiService.getData(`/v1/puzzle/wordwheel-tpl/${tplId}`),

    startPlay: async (wordWheelTplId) => {
        const { session, loggedIn } = await getSessionForRequest();
        const payload = { wordWheelTplId };
        if (session) {
            payload.sessionId = session.sessionId;
            payload.sessionCreatedAt = session.createdAt;
        }
        await attachOptionalRevenueCatIdentity(payload);
        const data = await postOptionalAuth("/v1/puzzle/wordwheel-play/start", payload);
        clearSessionAfterLoginMigration(loggedIn, session);
        return data;
    },

    updateProgress: async (wordWheelTplId, foundWords) => {
        const { session, loggedIn } = await getSessionForRequest();
        const payload = { wordWheelTplId, foundWords };
        if (session) {
            payload.sessionId = session.sessionId;
        }
        await attachOptionalRevenueCatIdentity(payload);
        const data = await putOptionalAuth("/v1/puzzle/wordwheel-play/progress", payload);
        clearSessionAfterLoginMigration(loggedIn, session);
        return data;
    },

    fetchCoinsCatalog: () => getOptionalAuth("/v1/puzzle/wordwheel/coins-catalog"),
};

export default WordWheelApi;
